import { Close, Download, Fullscreen, FullscreenExit } from '@mui/icons-material';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Box,
    Typography,
    Button,
    CircularProgress,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import SignatureBoard from './signatureBoard';
import Contract from 'components/common/AgreementSignature/Contract';
import Loader from 'components/v2/common/loader';

import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import type { AgreementDocumentInterface } from 'types';
import { getClientStampedSigning, isAgreementSigningAvailable } from 'utils/agreement';
import { downloadBlob } from 'utils/file';

interface AgreementViewerModalProps {
    title?: string;
    data?: AgreementDocumentInterface;
    loading: boolean;
    disabled?: boolean;
    showSignButton?: boolean;
    onClose: () => void;
    /**
     * `signedAgreement` is the pdf-lib stamped PDF for `client_stamped` retainers and
     * `null` for `server_embedded` ones, where only the drawn signature is submitted.
     */
    onSign: (signature: string, signedAgreement: Blob | null) => void;
}

const AgreementViewerModal = ({
    title = 'Document Preview',
    data,
    loading,
    disabled,
    showSignButton = false,
    onClose,
    onSign,
}: AgreementViewerModalProps) => {
    const dispatch = useDispatch();
    const { translate } = useTranslate();
    const [fullscreen, setFullscreen] = useState<boolean>(false);
    const [openSignatureBoard, setOpenSignatureBoard] = useState<boolean>(false);
    const [currentSignature, setCurrentSignature] = useState<string | null>(null);
    const [isSigning, setIsSigning] = useState(false);

    // Guard to ensure we only auto-upload once per signed blob
    const uploadedRef = useRef(false);
    const stampedRef = useRef<Blob | null>(null);
    // True once the parent has disabled the dialog for the in-flight sign request
    const requestStarted = useRef(false);

    const summary = data?.summary;
    const isServerEmbedded = summary?.signing?.method === 'server_embedded';
    const signaturePosition = getClientStampedSigning(summary);
    const canShowSignButton = showSignButton && isAgreementSigningAvailable(summary);
    const submitting = isSigning || Boolean(disabled);

    // Parent clears `disabled` when the sign request fails. Leave the pad open so they can retry.
    useEffect(() => {
        if (disabled) {
            requestStarted.current = true;
            return;
        }
        if (!requestStarted.current) return;
        requestStarted.current = false;
        uploadedRef.current = false;
        setIsSigning(false);
    }, [disabled]);

    const handleDownload = () => {
        if (submitting || !data?.file) return;
        const base = title.trim() || 'agreement';
        downloadBlob(data.file, base.toLowerCase().endsWith('.pdf') ? base : `${base}.pdf`);
    };

    const handleSignature = (signature: string) => {
        if (submitting || !canShowSignButton) return;
        setIsSigning(true);
        if (isServerEmbedded) {
            // No pdf-lib step: the backend embeds the signature and renders the PDF
            onSign(signature, null);
            return;
        }
        if (stampedRef.current && signature === currentSignature) {
            onSign(signature, stampedRef.current);
            return;
        }
        uploadedRef.current = false;
        setCurrentSignature(signature);
    };

    // Auto-upload as soon as Contract produces the stamped blob
    const handleSignedAgreement = useCallback(
        (blob: Blob) => {
            stampedRef.current = blob;
            if (uploadedRef.current || !currentSignature) return;
            uploadedRef.current = true;
            setIsSigning(true);
            onSign(currentSignature, blob);
        },
        [currentSignature, onSign]
    );

    const handleStampError = useCallback(() => {
        uploadedRef.current = false;
        stampedRef.current = null;
        setCurrentSignature(null);
        setIsSigning(false);
        dispatch(setAlert({ message: translate('somethingWentWrong'), type: 'error' }));
    }, [dispatch, translate]);

    const toggleFullscreen = () => {
        setFullscreen(!fullscreen);
    };

    return (
        <Dialog
            open
            maxWidth={false}
            fullWidth
            fullScreen={fullscreen}
            slotProps={{
                paper: {
                    sx: {
                        height: fullscreen ? '100dvh' : '92dvh',
                        width: fullscreen ? '100vw' : '65vw',
                        maxWidth: fullscreen ? '100vw' : '95vw',
                        minWidth: { xs: '95vw', md: '60vw' },
                        m: fullscreen ? 0 : 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    },
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: { xs: 1.5, sm: 0 },
                    py: 1.5,
                    px: 2,
                }}
            >
                <Typography
                    variant="h6"
                    component="div"
                    sx={{
                        fontWeight: 600,
                        pr: { xs: 0, sm: 2 },
                        width: { xs: '100%', sm: 'auto' },
                    }}
                >
                    {title}
                </Typography>
                <Box
                    sx={{
                        display: 'flex',
                        gap: 1,
                        flexShrink: 0,
                        width: { xs: '100%', sm: 'auto' },
                        justifyContent: { xs: 'flex-end', sm: 'flex-start' },
                        flexWrap: { xs: 'wrap', sm: 'nowrap' },
                        alignItems: 'center',
                    }}
                >
                    {canShowSignButton && !loading && (
                        <Button
                            variant="contained"
                            color="warning"
                            disableRipple
                            size="small"
                            disabled={submitting}
                            startIcon={
                                submitting ? (
                                    <CircularProgress size={14} color="inherit" />
                                ) : undefined
                            }
                            sx={{
                                '&:hover': { color: '#fff' },
                                '&.MuiButton-contained:not(.Mui-disabled)': { color: '#fff' },
                            }}
                            onClick={() => {
                                uploadedRef.current = false;
                                stampedRef.current = null;
                                setCurrentSignature(null);
                                setOpenSignatureBoard(true);
                            }}
                        >
                            {submitting
                                ? translate('signingAgreement')
                                : translate('signAgreement')}
                        </Button>
                    )}
                    {summary?.pdf_url && (
                        <IconButton
                            onClick={handleDownload}
                            edge="end"
                            size="small"
                            disabled={submitting}
                            aria-label={translate('download')}
                        >
                            <Download fontSize="small" />
                        </IconButton>
                    )}
                    <IconButton
                        onClick={toggleFullscreen}
                        edge="end"
                        size="small"
                        disabled={submitting}
                    >
                        {fullscreen ? (
                            <FullscreenExit fontSize="small" />
                        ) : (
                            <Fullscreen fontSize="small" />
                        )}
                    </IconButton>
                    <IconButton
                        onClick={onClose}
                        edge="end"
                        disabled={loading || submitting}
                        size="small"
                    >
                        <Close fontSize="small" />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent
                dividers
                sx={{
                    p: 0,
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    overflow: 'hidden',
                    minHeight: 0,
                    bgcolor: fullscreen ? 'grey.100' : 'background.paper',
                }}
            >
                {loading ? (
                    <Loader rootSx={{ minHeight: '300px' }} message="Loading document..." />
                ) : data ? (
                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            overflow: 'auto',
                            display: 'flex',
                            justifyContent: 'center',
                            px: fullscreen ? { xs: 1.5, md: 4 } : 0,
                            py: fullscreen ? { xs: 1.5, md: 3 } : 0,
                        }}
                    >
                        <Box sx={{ width: '100%', maxWidth: fullscreen ? 1100 : '100%' }}>
                            <Contract
                                file={data.file}
                                signature={currentSignature}
                                signaturePosition={signaturePosition}
                                onSignedAgreement={handleSignedAgreement}
                                onStampError={handleStampError}
                            />
                        </Box>
                    </Box>
                ) : null}

                {openSignatureBoard && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            backgroundColor: '#ffffff',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            zIndex: 1300,
                            overflow: 'auto',
                        }}
                    >
                        <SignatureBoard
                            isSubmitting={submitting}
                            onClose={() => {
                                if (submitting) return;
                                setOpenSignatureBoard(false);
                            }}
                            onSign={handleSignature}
                        />
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default AgreementViewerModal;
